import React from 'react';
import {
  IconChevronLeft,
  IconChevronRight,
  IconFolders,
  IconSearch,
} from '@tabler/icons-react';
import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Text } from 'src/design/atoms/Text';
import { Badge } from 'src/design/atoms/Badge';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { Breadcrumbs } from 'src/design/atoms/Breadcrumbs';
import { CloseButton } from 'src/design/atoms/CloseButton';
import { TextInput } from 'src/design/atoms/TextInput';
import { UnstyledButton } from 'src/design/atoms/UnstyledButton';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { AccessIndicator } from 'src/design/molecules/AccessIndicator';
import { DocumentDto } from 'src/api/FiretrolApiClient';
import { accessKindForFolder } from 'src/app/documents/documentAccess';
import {
  DOCUMENT_FOLDER_TYPE,
  ROOT_FOLDER_ID,
  isRestrictedAccess,
} from 'src/app/documents/documentConstants';
import { useAuth } from 'src/hooks/useAuth';
import classes from './FolderHeader.module.css';

export interface IFolderHeaderProps {
  breadcrumbs: DocumentDto[];
  currentFolder: DocumentDto | null;
  isSelectedLocked: boolean;
  canDeleteFolder: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  onNavigate: (folderId: string) => void;
  onEditFolder: () => void;
  onDeleteFolder: () => void | Promise<void>;
  onOpenSidebar?: () => void;
}

const ROOT_LABEL = 'All documents';

export const FolderHeader: React.FC<IFolderHeaderProps> = ({
  breadcrumbs,
  currentFolder,
  isSelectedLocked,
  canDeleteFolder,
  search,
  onSearchChange,
  onNavigate,
  onEditFolder,
  onDeleteFolder,
  onOpenSidebar,
}) => {
  const { user } = useAuth();

  const crumbs = [
    { id: ROOT_FOLDER_ID, name: ROOT_LABEL },
    ...breadcrumbs.map((crumb) => ({ id: crumb.id, name: crumb.name ?? '' })),
  ];
  const ancestors = crumbs.slice(0, -1);
  const current = crumbs[crumbs.length - 1];
  const parent = ancestors[ancestors.length - 1] ?? null;

  const access = currentFolder
    ? accessKindForFolder(currentFolder, user?.id ?? -1)
    : null;

  return (
    <Box px="md" py="xs" className={classes.header}>
      <Group gap="xs" wrap="nowrap" className={classes.trail}>
        {onOpenSidebar && (
          <BasicActionIcon
            hiddenFrom="md"
            variant="subtle"
            aria-label="Browse folders"
            onClick={onOpenSidebar}
          >
            <IconFolders size={18} />
          </BasicActionIcon>
        )}

        {ancestors.length > 0 && (
          <Group
            gap="xs"
            wrap="nowrap"
            visibleFrom="sm"
            className={classes.ancestors}
          >
            <Breadcrumbs
              className={classes.breadcrumbs}
              separator={
                <IconChevronRight
                  size={14}
                  color="var(--mantine-color-dimmed)"
                />
              }
            >
              {ancestors.map((crumb) => (
                <UnstyledButton
                  key={crumb.id}
                  className={classes.crumb}
                  onClick={() => onNavigate(crumb.id)}
                >
                  <Text size="sm" c="dimmed" truncate>
                    {crumb.name}
                  </Text>
                </UnstyledButton>
              ))}
            </Breadcrumbs>
            <IconChevronRight size={14} color="var(--mantine-color-dimmed)" />
          </Group>
        )}

        {parent && (
          <BasicActionIcon
            hiddenFrom="sm"
            variant="subtle"
            aria-label={`Up to ${parent.name}`}
            onClick={() => onNavigate(parent.id)}
          >
            <IconChevronLeft size={16} />
          </BasicActionIcon>
        )}

        <Text size="sm" fw={600} c="bright" truncate className={classes.crumb}>
          {current.name}
        </Text>

        {access &&
          currentFolder &&
          isRestrictedAccess(currentFolder.access) && (
            <AccessIndicator kind={access.kind} people={access.people} />
          )}

        {isSelectedLocked && (
          <Badge variant="light" color="gray" size="sm">
            System
          </Badge>
        )}

        {currentFolder?.folderType === DOCUMENT_FOLDER_TYPE.emails && (
          <Badge variant="light" color="gray" size="sm">
            Email inbox
          </Badge>
        )}
      </Group>

      <TextInput
        size="xs"
        className={classes.search}
        value={search}
        aria-label="Search this folder"
        placeholder="Search this folder"
        leftSection={<IconSearch size={14} />}
        rightSection={
          search ? (
            <CloseButton
              size="sm"
              aria-label="Clear search"
              onClick={() => onSearchChange('')}
            />
          ) : null
        }
        onChange={(event) => onSearchChange(event.currentTarget.value)}
      />

      {currentFolder && (
        <Group gap="xs" wrap="nowrap" className={classes.folderActions}>
          <EditActionIcon label="Edit folder" onClick={onEditFolder} />
          {canDeleteFolder && (
            <DeleteActionIcon
              label="Delete folder"
              confirmationTitle="Delete folder?"
              confirmationText="All subfolders and documents inside it will also be deleted."
              onDelete={onDeleteFolder}
            />
          )}
        </Group>
      )}
    </Box>
  );
};
